/**
 * RCS onboarding domain types — kept aligned with the backend snapshot stored
 * in `shopRequiredData_rcs_business`. Any change here must be mirrored in
 * `libs/common/src/templates/rcs-onboarding.types.ts` on the NestJS side.
 */

export enum RcsSenderMode {
  SHARED_SHOPIMIND = 'shared_shopimind',
  DEDICATED = 'dedicated',
}

export enum RcsOnboardingStatus {
  NOT_STARTED = 'not_started',
  ACTIVATED = 'activated',
  SHARED_ACTIVE = 'shared_active',
  BRAND_CREATED = 'brand_created',
  AGENT_DRAFTED = 'agent_drafted',
  AGENT_LINKED = 'agent_linked',
  PENDING_APPROVAL = 'pending_approval',
  LAUNCHED = 'launched',
  REJECTED = 'rejected',
  FAILED_BRAND = 'failed_brand',
  FAILED_AGENT = 'failed_agent',
}

export type RcsSenderSource = 'shared' | 'dedicated' | 'fallback_shared';

export interface RcsBrand {
  /** Vonage brand id — only present once the brand has been created remotely. */
  id?: string;
  display_name: string;
}

export interface RcsAgentPreviewUrl {
  media_type: 'image' | 'video';
  url: string;
}

export interface RcsPointOfContact {
  first_name: string;
  last_name: string;
  job_title: string;
  email: string;
  brand_website: string;
}

/** A carrier (mobile operator) the agent will be launched on, from Vonage. */
export interface RcsSelectedCarrier {
  /** Vonage carrier id, e.g. "orange-fr". */
  id: string;
  /** Display name, e.g. "Orange". */
  carrier: string;
  /** ISO country code where the carrier operates, e.g. "FR". */
  country_letter_code: string;
}

export interface RcsCarrierSelection {
  carriers: RcsSelectedCarrier[];
}

export interface RcsTestNumber {
  number: string;
}

export interface RcsTestNumbers {
  numbers: RcsTestNumber[];
}

export interface RcsCarrierRequirements {
  agent_preview: {
    agent_access_instructions: string;
    urls: RcsAgentPreviewUrl[];
  };
  points_of_contact: RcsPointOfContact[];
  traffic_estimates: {
    average_global_traffic: string;
    average_message_rate_per_user: string;
    users_targeted: string;
  };
  user_experience: {
    interactions_description: string;
    optin_description: string;
    optout_description: string;
    trigger_description: string;
  };
}

export interface RcsVisualWebsiteEntry {
  address: string;
  label: string;
}

export interface RcsVisualDesign {
  brand_color: string;
  tagline: string;
  /** Public URL to the agent logo (Vonage visual_design.logo_image). */
  logo_image: string;
  /** Public URL to the agent banner (Vonage visual_design.banner_image). */
  banner_image: string;
  email: {
    address: string;
    label: string;
  };
  phone: {
    number: string;
    label: string;
  };
  website: RcsVisualWebsiteEntry[];
  privacy_policy: string;
  terms_of_service: string;
}

/** Legacy flat visual shape (pre–visual_design Vonage alignment). */
export type RcsVisualDesignSnapshotInput = Partial<RcsVisualDesign> & {
  color?: string;
  website?: string | RcsVisualWebsiteEntry[];
  logo_url?: string;
  logo_image?: string;
  banner_image?: string;
};

/** Last successfully pushed remote profile sections (used for is_dirty detection). */
export interface RcsAgentRemoteBaseline {
  sender_id?: string;
  display_name?: string;
  basic_info?: {
    agent_purpose: string;
    hosting_region: string;
    billing_category: string;
  };
  brand?: RcsBrand;
  carrier_selection?: RcsCarrierSelection;
  carrier_requirements?: RcsCarrierRequirements;
  visual?: RcsVisualDesignSnapshotInput;
  representative?: RcsRepresentativeSnapshotInput;
}

export interface RcsAgent {
  /** Stable client-side id, assigned at draft creation (before Vonage id). */
  local_id: string;
  /** Vonage agent id — present once the agent has been created remotely. */
  id?: string;
  /**
   * Brand owned by this agent. Agents may share the same Vonage brand id
   * (reused via the wizard dropdown); a fresh brand has no id until created.
   */
  brand?: RcsBrand;
  sender_id: string;
  display_name: string;
  state: string;
  /** Per-agent lifecycle status. */
  onboarding_status: RcsOnboardingStatus;
  basic_info?: {
    agent_purpose: string;
    hosting_region: string;
    billing_category: string;
  };
  carrier_selection?: RcsCarrierSelection;
  test_numbers?: RcsTestNumbers;
  carrier_requirements?: RcsCarrierRequirements;
  visual?: RcsVisualDesignSnapshotInput;
  representative?: RcsRepresentativeSnapshotInput;
  /** True when local data differs from the last remote sync. */
  is_dirty?: boolean;
  remote_baseline?: RcsAgentRemoteBaseline;
}

export interface RcsRepresentative {
  first_name: string;
  last_name: string;
  email: string;
  brand_website: string;
}

/** Legacy representative shape (name + phone). */
export type RcsRepresentativeSnapshotInput = Partial<RcsRepresentative> & {
  name?: string;
  phone?: string;
};

export interface RcsSelectableSender {
  sender_id: string;
  display_name: string;
  source: 'shared' | 'dedicated';
}

export interface RcsOnboardingStatusPayload {
  activated: boolean;
  agents: RcsAgent[];
  shared_sender: RcsSelectableSender;
  selectable_senders: RcsSelectableSender[];
}

/**
 * Distinct, already-created brands (with a Vonage id) across the shop's agents.
 * Powers the "reuse an existing brand" dropdown in the basic-info step.
 */
export const getRcsReusableBrands = (agents: RcsAgent[]): RcsBrand[] => {
  const seen = new Set<string>();
  const brands: RcsBrand[] = [];
  agents.forEach((agent) => {
    const brand = agent.brand;
    if (brand?.id && !seen.has(brand.id)) {
      seen.add(brand.id);
      brands.push({ id: brand.id, display_name: brand.display_name });
    }
  });
  return brands;
};

export const RCS_AGENT_PURPOSE_DEFAULT = 'MULTI_USE';
export const RCS_HOSTING_REGION_DEFAULT = 'EUROPE';
export const RCS_BILLING_CATEGORY_DEFAULT = 'CONVERSATIONAL';

export const RCS_HOSTING_REGIONS = ['EUROPE'] as const;

export const RCS_PREVIEW_MEDIA_TYPES = ['image', 'video'] as const;

export const RCS_AVERAGE_GLOBAL_TRAFFIC = [
  'THOUSAND',
  'TEN_THOUSAND',
  'FIFTY_THOUSAND',
  'HUNDRED_THOUSAND',
  'MILLION',
  'MILLION_AND_MORE',
] as const;

export const RCS_AVERAGE_MESSAGE_RATE_PER_USER = [
  'ONCE_PER_MONTH',
  '2_4_TIMES_PER_MONTH',
  'ONCE_PER_WEEK',
  '2_6_TIMES_PER_WEEK',
  'DAILY',
  'MULTIPLE_TIMES_PER_DAY',
] as const;

export const RCS_USERS_TARGETED = [
  'HUNDRED',
  'THOUSAND',
  'TEN_THOUSAND',
  'HUNDRED_THOUSAND',
  'HALF_MILLION',
  'MILLION',
  'MILLION_AND_MORE',
] as const;

export const RCS_USER_EXPERIENCE_MAX_LENGTH = 10000;
export const RCS_VISUAL_TAGLINE_MAX_LENGTH = 100;
export const RCS_VISUAL_LABEL_MAX_LENGTH = 25;
export const RCS_DEFAULT_BRAND_COLOR = '#94c840';

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const E164_WITHOUT_PLUS_REGEX = /^[1-9]\d{1,14}$/;
const E164_WITH_PLUS_REGEX = /^\+[1-9]\d{1,14}$/;
const URL_WITH_PROTOCOL_REGEX = /^https?:\/\/.+/i;

export const normalizeRcsPhoneNumber = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '');
  return digits;
};

export const normalizeRcsTestDevicePhoneNumber = (value: string): string => {
  const trimmed = (value || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  return `+${digits}`;
};

export const createEmptyVisualDesign = (): RcsVisualDesign => ({
  brand_color: RCS_DEFAULT_BRAND_COLOR,
  tagline: '',
  logo_image: '',
  banner_image: '',
  email: { address: '', label: '' },
  phone: { number: '', label: '' },
  website: [{ address: '', label: '' }],
  privacy_policy: '',
  terms_of_service: '',
});

export const hydrateVisualDesign = (
  target: RcsVisualDesign,
  source?: RcsVisualDesignSnapshotInput,
): void => {
  if (!source) return;
  const empty = createEmptyVisualDesign();
  target.brand_color = source.brand_color || source.color || empty.brand_color;
  target.tagline = source.tagline || empty.tagline;
  target.logo_image = source.logo_image || source.logo_url || empty.logo_image;
  target.banner_image = source.banner_image || empty.banner_image;
  if (source.email) {
    target.email.address = source.email.address || '';
    target.email.label = source.email.label || '';
  }
  if (source.phone) {
    target.phone.number = source.phone.number || '';
    target.phone.label = source.phone.label || '';
  }
  if (Array.isArray(source.website) && source.website.length) {
    target.website = source.website.map((entry) => ({
      address: entry.address || '',
      label: entry.label || '',
    }));
  } else if (typeof source.website === 'string' && source.website.trim()) {
    target.website = [{ address: source.website.trim(), label: '' }];
  }
  target.privacy_policy = source.privacy_policy || empty.privacy_policy;
  target.terms_of_service = source.terms_of_service || empty.terms_of_service;
};

export const isVisualDesignComplete = (visual: RcsVisualDesign): boolean => {
  if (!HEX_COLOR_REGEX.test((visual.brand_color || '').trim())) {
    return false;
  }
  const tagline = visual.tagline?.trim() || '';
  if (!tagline || tagline.length > RCS_VISUAL_TAGLINE_MAX_LENGTH) {
    return false;
  }
  if (!/.+@.+\..+/.test(visual.email?.address?.trim() || '')) {
    return false;
  }
  if (!E164_WITHOUT_PLUS_REGEX.test(normalizeRcsPhoneNumber(visual.phone?.number || ''))) {
    return false;
  }
  if (!URL_WITH_PROTOCOL_REGEX.test((visual.logo_image || '').trim())) {
    return false;
  }
  if (!URL_WITH_PROTOCOL_REGEX.test((visual.banner_image || '').trim())) {
    return false;
  }
  const websites = (visual.website || []).filter((entry) => !!entry.address?.trim());
  if (!websites.length) {
    return false;
  }
  if (!websites.every((entry) => URL_WITH_PROTOCOL_REGEX.test(entry.address.trim()))) {
    return false;
  }
  const optionalUrls = [visual.privacy_policy, visual.terms_of_service]
    .map((value) => value?.trim())
    .filter(Boolean) as string[];
  return optionalUrls.every((url) => URL_WITH_PROTOCOL_REGEX.test(url));
};

/** Fixed values sent to carriers — not editable in the wizard UI. */
export const RCS_DEFAULT_AGENT_ACCESS_INSTRUCTIONS =
  'Provide a method for reviewers to trigger the agent, such as a website where reviewers can invite and add phone numbers. If you need a list of phone numbers to add as test devices, contact rbm-support@vonage.com. Test the agent by sending a keyword like \'HELP\' or \'START\' to initiate conversation flow.';

export const RCS_DEFAULT_INTERACTIONS_DESCRIPTION =
  'This agent handles customer support inquiries and provides product information. Primary interactions include order status updates, account notifications, and FAQ responses. Secondary interactions may include promotional offers for existing customers and appointment scheduling assistance.';

export const RCS_DEFAULT_OPTIN_DESCRIPTION =
  'This is a test environment. Customers consent to receive messages directly via request, phone, or email. Users can opt-in by visiting our website and providing their phone number, or by texting \'JOIN\' to our SMS shortcode. Opt-in confirmation includes details about message frequency and content type.';

export const RCS_DEFAULT_TRIGGER_DESCRIPTION =
  'This account is used exclusively for sending test messages to introduce RCS capabilities to customers. Triggers include: order placement confirmations, shipping notifications, appointment reminders, customer service escalations, and weekly promotional campaigns for opted-in users.';

export const RCS_DEFAULT_OPTOUT_DESCRIPTION =
  'Customers can unsubscribe from receiving messages by replying \'STOP\' to any message.';

export const applyCarrierHiddenDefaults = (
  carrier: RcsCarrierRequirements,
): void => {
  carrier.agent_preview.agent_access_instructions =
    RCS_DEFAULT_AGENT_ACCESS_INSTRUCTIONS;
  carrier.agent_preview.urls = [];
  carrier.user_experience.interactions_description =
    RCS_DEFAULT_INTERACTIONS_DESCRIPTION;
  carrier.user_experience.optin_description =
    RCS_DEFAULT_OPTIN_DESCRIPTION;
  carrier.user_experience.trigger_description =
    RCS_DEFAULT_TRIGGER_DESCRIPTION;
  carrier.user_experience.optout_description =
    RCS_DEFAULT_OPTOUT_DESCRIPTION;
};

export interface RcsDedicatedFormState {
  basic_info: {
    /** Vonage brand id when reusing an existing brand; empty for a new brand. */
    brand_id: string;
    brand_display_name: string;
    agent_display_name: string;
    sender_id: string;
    agent_purpose: string;
    hosting_region: string;
    billing_category: string;
  };
  carrier_selection: RcsCarrierSelection;
  test_numbers: RcsTestNumbers;
  carrier_requirements: RcsCarrierRequirements;
  visual: RcsVisualDesign;
  representative: RcsRepresentative;
}

export const RCS_REPRESENTATIVE_NAME_MAX_LENGTH = 200;

export const createEmptyPointOfContact = (): RcsPointOfContact => ({
  first_name: '',
  last_name: '',
  job_title: '',
  email: '',
  brand_website: '',
});

export const createEmptyCarrierRequirements = (): RcsCarrierRequirements => {
  const carrier: RcsCarrierRequirements = {
    agent_preview: {
      agent_access_instructions: '',
      urls: [],
    },
    points_of_contact: [createEmptyPointOfContact()],
    traffic_estimates: {
      average_global_traffic: '',
      average_message_rate_per_user: '',
      users_targeted: '',
    },
    user_experience: {
      interactions_description: '',
      optin_description: '',
      optout_description: '',
      trigger_description: '',
    },
  };
  applyCarrierHiddenDefaults(carrier);
  return carrier;
};

export const createEmptyRepresentative = (): RcsRepresentative => ({
  first_name: '',
  last_name: '',
  email: '',
  brand_website: '',
});

export const createEmptyCarrierSelection = (): RcsCarrierSelection => ({
  carriers: [],
});

export const createEmptyTestNumbers = (): RcsTestNumbers => ({
  numbers: [{ number: '' }],
});

export const createEmptyRcsForm = (): RcsDedicatedFormState => ({
  basic_info: {
    brand_id: '',
    brand_display_name: '',
    agent_display_name: '',
    sender_id: '',
    agent_purpose: RCS_AGENT_PURPOSE_DEFAULT,
    hosting_region: RCS_HOSTING_REGION_DEFAULT,
    billing_category: RCS_BILLING_CATEGORY_DEFAULT,
  },
  carrier_selection: createEmptyCarrierSelection(),
  test_numbers: createEmptyTestNumbers(),
  carrier_requirements: createEmptyCarrierRequirements(),
  visual: createEmptyVisualDesign(),
  representative: createEmptyRepresentative(),
});

export const hydrateRepresentative = (
  target: RcsRepresentative,
  source?: RcsRepresentativeSnapshotInput,
): void => {
  if (!source) return;
  if (source.first_name?.trim() || source.last_name?.trim()) {
    target.first_name = source.first_name?.trim() || '';
    target.last_name = source.last_name?.trim() || '';
  } else if (source.name?.trim()) {
    const parts = source.name.trim().split(/\s+/);
    target.first_name = parts[0] || '';
    target.last_name = parts.slice(1).join(' ') || '';
  }
  target.email = source.email?.trim() || '';
  target.brand_website = source.brand_website?.trim() || '';
};

export const isRepresentativeComplete = (
  representative: RcsRepresentative,
): boolean => {
  const firstName = representative.first_name?.trim() || '';
  const lastName = representative.last_name?.trim() || '';
  if (!firstName || firstName.length > RCS_REPRESENTATIVE_NAME_MAX_LENGTH) {
    return false;
  }
  if (!lastName || lastName.length > RCS_REPRESENTATIVE_NAME_MAX_LENGTH) {
    return false;
  }
  if (!/.+@.+\..+/.test(representative.email?.trim() || '')) {
    return false;
  }
  return URL_WITH_PROTOCOL_REGEX.test(representative.brand_website?.trim() || '');
};

const isNonEmpty = (value?: string): boolean => !!value?.trim();

export const isPointOfContactValid = (contact: RcsPointOfContact): boolean => (
  isNonEmpty(contact.first_name)
  && isNonEmpty(contact.last_name)
  && isNonEmpty(contact.job_title)
);

export const isCarrierRequirementsComplete = (
  carrier: RcsCarrierRequirements,
): boolean => {
  const contactsOk = carrier.points_of_contact.length > 0
    && carrier.points_of_contact.every(isPointOfContactValid);
  const traffic = carrier.traffic_estimates;
  const trafficOk = !!traffic.average_global_traffic
    && !!traffic.average_message_rate_per_user
    && !!traffic.users_targeted;
  return contactsOk && trafficOk;
};

/** At least one carrier must be selected before the agent can be launched. */
export const isCarrierSelectionComplete = (
  selection: RcsCarrierSelection,
): boolean => (selection?.carriers?.length || 0) > 0;

export const sanitizeTestNumbersForApi = (
  testNumbers: RcsTestNumbers,
): RcsTestNumbers => ({
  numbers: (testNumbers?.numbers || [])
    .map((entry) => ({
      number: normalizeRcsTestDevicePhoneNumber(entry?.number || ''),
    }))
    .filter((entry) => E164_WITH_PLUS_REGEX.test(entry.number)),
});

export const isTestNumbersComplete = (
  testNumbers: RcsTestNumbers,
): boolean => sanitizeTestNumbersForApi(testNumbers).numbers.length > 0;

export const RCS_WIZARD_STEPS = [
  'basic_info',
  'carrier_selection',
  'visual',
  'representative',
  'carrier_requirements',
  'test_numbers',
] as const;

export type RcsWizardStepId = (typeof RCS_WIZARD_STEPS)[number];

export type RcsAgentCardState =
  | 'draft'
  | 'pending'
  | 'active'
  | 'rejected'
  | 'failed';

/**
 * Derive the card badge state shown in the agents list from an agent's
 * lifecycle status. Drafts (and never-provisioned agents) are editable.
 */
export const getRcsAgentCardState = (agent: RcsAgent): RcsAgentCardState => {
  switch (agent.onboarding_status) {
    case RcsOnboardingStatus.LAUNCHED:
      return 'active';
    case RcsOnboardingStatus.REJECTED:
      return 'rejected';
    case RcsOnboardingStatus.FAILED_BRAND:
    case RcsOnboardingStatus.FAILED_AGENT:
      return 'failed';
    case RcsOnboardingStatus.AGENT_DRAFTED:
    case RcsOnboardingStatus.AGENT_LINKED:
    case RcsOnboardingStatus.PENDING_APPROVAL:
      return 'pending';
    default:
      return 'draft';
  }
};

/**
 * Agent can be opened in the wizard (local edits allowed).
 * Includes `pending` (provisioned, awaiting approval) so merchants can still
 * adjust branding before applying changes remotely.
 */
export const isRcsAgentEditable = (agent: RcsAgent): boolean => {
  const cardState = getRcsAgentCardState(agent);
  return cardState === 'draft'
    || cardState === 'pending'
    || cardState === 'rejected'
    || cardState === 'failed'
    || cardState === 'active';
};

/** True when local snapshot differs from the last remote profile sync. */
export const isRcsAgentPendingRemoteSync = (agent: RcsAgent): boolean => (
  !!agent.is_dirty
);

/** First-time publish (no remote agent id yet). */
export const isRcsAgentAwaitingPublish = (agent: RcsAgent): boolean => (
  !agent.id && isRcsAgentPendingRemoteSync(agent)
);

/** Local-only drafts can be removed; provisioned agents cannot. */
export const isRcsAgentDeletable = (agent: RcsAgent): boolean => !agent.id;
